const snapshot = (value) => JSON.parse(JSON.stringify(value || {}));

// The store supplies all effects so settings writes have one serial owner.
// Explicit patches are adopted only after acknowledgment (logo/setup completion).
export function createSettingsSaveOwner({
    context,
    settings,
    save,
    adopt,
    status,
    revision,
    equal,
    failure
}) {
    let generation = 0;
    let activeContext = context();
    let pending = null;
    let requiredPatch = {};
    let running = false;
    let requestVersion = 0;
    let waiters = [];

    function reset() {
        generation += 1;
        activeContext = context();
        pending = null;
        requiredPatch = {};
        running = false;
        const oldWaiters = waiters;
        waiters = [];
        for (const waiter of oldWaiters) waiter.reject({ cancelled: true });
        status({ saving: false, dirty: false, error: "" });
    }

    function capture(patch) {
        const draft = snapshot(settings());
        return {
            draft,
            patch: snapshot(patch),
            settings: { ...draft, ...patch },
            version: requestVersion
        };
    }

    async function drain() {
        if (running) return;
        running = true;
        const attempt = generation;
        const isCurrent = () => attempt === generation && activeContext === context();
        status({ saving: true, dirty: true, error: "" });
        while (pending && isCurrent()) {
            const submission = pending;
            pending = null;
            try {
                const result = await save(submission.settings);
                if (!isCurrent()) return;
                revision();
                const currentDraft = snapshot(settings());
                const canonical = snapshot(result?.settings || submission.settings);
                const nextDraft = equal(currentDraft, submission.draft)
                    ? canonical
                    : { ...canonical, ...currentDraft, ...submission.patch };
                adopt({ ...result, settings: canonical }, nextDraft);
                for (const [key, value] of Object.entries(submission.patch)) {
                    if (equal(requiredPatch[key], value)) delete requiredPatch[key];
                }
                const acknowledged = waiters.filter(
                    (waiter) => waiter.version <= submission.version
                );
                waiters = waiters.filter((waiter) => waiter.version > submission.version);
                for (const waiter of acknowledged) waiter.resolve(result);
                if (pending) {
                    // New edits replace the queued body, keeping the server's
                    // acknowledged defaults and any outstanding explicit patch.
                    pending = capture(requiredPatch);
                }
            } catch (error) {
                if (!isCurrent()) return;
                pending = capture(requiredPatch);
                const failed = waiters;
                waiters = [];
                status({
                    saving: false,
                    dirty: true,
                    error: failure(
                        error,
                        failed.some((waiter) => waiter.notify)
                    )
                });
                running = false;
                for (const waiter of failed) waiter.reject(error);
                return;
            }
        }
        if (isCurrent()) {
            running = false;
            status({ saving: false, dirty: false, error: "" });
        }
    }

    function request({ patch = {}, notify = true } = {}) {
        if (activeContext !== context()) reset();
        requestVersion += 1;
        revision();
        requiredPatch = { ...requiredPatch, ...snapshot(patch) };
        pending = capture(requiredPatch);
        const promise = new Promise((resolve, reject) => {
            waiters.push({ version: requestVersion, notify, resolve, reject });
        });
        void drain();
        return promise;
    }

    return { request, reset, isDirty: () => running || Boolean(pending) };
}
